/**
 * ITSM Columns for TanStackTable
 *
 * Provides column definitions for IT Service Management (Jira, ServiceNow) notifications.
 *
 * Columns:
 * 1. Integration - Ticket icon + Provider name + Display name + Copy icon
 * 2. Severity Threshold - Severity badge with tooltip
 * 3. Actions - Ellipsis menu with Edit (Jira only) + Delete (both)
 *
 * Conditional Edit:
 * - Jira has `edit: true` in AllIntegrations, so it shows Edit menu item
 * - ServiceNow has no edit flag, so it only shows Delete
 */
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon, TicketIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { CellAlignment, Column } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { AllIntegrations } from '@/hooks/useIntegration';
import { NOTIFICATION_TABLE_COLUMN_WIDTHS } from '@/sections/settings/components/cards/notifications/constants';
import { SeverityThresholdCell } from '@/sections/settings/components/cells/SeverityThresholdCell';
import { CopyIconButton } from '@/sections/settings/components/primitives/CopyIconButton';
import {
  RowAction,
  RowActionsMenu,
} from '@/sections/settings/components/primitives/RowActionsMenu';
import { Account, Integration } from '@/types';

interface ITSMColumnsConfig {
  /** Handler for viewing/editing ITSM integration (Jira only shows Edit) */
  onView: (integration: Account) => void;
  /** Handler for deleting ITSM integration (hidden when undefined) */
  onDelete?: (integration: Account) => void;
}

/**
 * Column definitions for ITSM TanStackTable.
 *
 * Provides:
 * - Integration column with provider icon, name, display name, and copy icon
 * - Severity threshold column with badge
 * - Actions column with conditional Edit (Jira only) and Delete
 */
export function getItsmColumns({
  onView,
  onDelete,
}: ITSMColumnsConfig): Column<Account>[] {
  return [
    {
      id: 'integration' as keyof Account,
      label: 'Integration',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.primaryValue,
      cell: item => {
        const integrationMeta = AllIntegrations[item.member as Integration];
        const providerName = integrationMeta?.name || 'ITSM';
        const displayName =
          item.settings?.displayName || 'Create and manage tickets';

        // Get current theme for icon path
        const currentTheme = document.documentElement.classList.contains('dark')
          ? 'dark'
          : 'light';

        return (
          <div className="flex items-center gap-2">
            {integrationMeta?.logo ? (
              <img
                src={`/icons/${currentTheme}/${integrationMeta.logo}`}
                alt={integrationMeta.name}
                className="size-5 shrink-0"
              />
            ) : (
              <TicketIcon className="text-chariot-text-tertiary size-5 shrink-0" />
            )}
            <span className="text-chariot-text-primary truncate text-sm">
              {providerName} - {displayName}
            </span>
            {item.value && (
              <CopyIconButton
                value={item.value}
                label="Webhook URL"
                data-testid={`copy-itsm-${item.key}`}
              />
            )}
          </div>
        );
      },
    },
    {
      id: 'severities' as keyof Account,
      label: (
        <span className="flex items-center gap-1">
          Severity Threshold
          <Tooltip title="Minimum vulnerability severity level that will trigger ITSM ticket creation">
            <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0 cursor-pointer" />
          </Tooltip>
        </span>
      ),
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.severities,
      cell: item => {
        // For JIRA: Read from first template's severities field
        // For other ITSM: Read from config or settings fallbacks
        const template = item.settings?.templates?.[0];
        const threshold =
          template?.severities || // JIRA template severity
          (item.config?.severities as string) || // Fallback for other ITSM
          item.settings?.severityThreshold || // Generic severity
          'MHC'; // Default

        return (
          <SeverityThresholdCell
            threshold={threshold}
            data-testid={`severity-${item.key}`}
          />
        );
      },
    },
    {
      id: '' as keyof Account,
      label: '',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.actions,
      align: 'right' as CellAlignment,
      cell: item => {
        const integrationMeta = AllIntegrations[item.member as Integration];
        const hasEdit = integrationMeta?.edit === true;

        // Build actions array conditionally
        const actions: RowAction[] = [];

        // Conditional Edit - Jira only
        if (hasEdit) {
          actions.push({
            label: 'Edit',
            icon: <PencilSquareIcon className="size-4" />,
            onClick: () => onView(item),
          });
        }

        // Delete - visible when onDelete is provided
        if (onDelete) {
          actions.push({
            label: 'Delete',
            icon: <TrashIcon className="size-4" />,
            onClick: () => onDelete(item),
            variant: 'danger',
          });
        }

        return (
          <div className="flex justify-end">
            <RowActionsMenu
              testIdPrefix="itsm"
              itemKey={item.key}
              actions={actions}
            />
          </div>
        );
      },
    },
  ];
}
